import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogTitle from '@material-ui/core/DialogTitle/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent/DialogContent';
import MuiDialogActions from '@material-ui/core/DialogActions';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import {withStyles, TextField, Button, CircularProgress} from '@material-ui/core';
import {Alert} from '@material-ui/lab';
import {getProfile} from '../../../../../redux/actions/User/Profile/profileAction';
import {
    manualPaymentCancel,
    clearManualPaymentCancel,
} from '../../../../../redux/actions/Payment/ManualPaymentCancel/manualPaymentCancelActions';
import {c, statusLabel, StatusPill} from './paymentAuditShared';

const ALLOWED_EMAILS = [
    'cemile@extranetwork.com',
    'bulut@extranetwork.com',
    'yoy@extranetwork.com',
    'onur@extranetwork.com',
    'cem@extranetwork.com',
    'partnerships@extranetwork.com',
    'zehra@extranetwork.com'
];

const sx = {
    infoBox: {
        display: 'flex', gap: 10, alignItems: 'flex-start',
        background: '#eff6ff', border: '1px solid #dbeafe', borderRadius: 10,
        padding: '12px 14px', marginBottom: 20,
    },
    card: {
        border: `1px solid ${c.line}`, borderRadius: 12, padding: 20,
        background: '#fff', marginBottom: 20,
    },
    fieldLabel: {
        display: 'block', fontSize: 12, fontWeight: 700, letterSpacing: '.02em',
        textTransform: 'uppercase', color: c.sub, marginBottom: 8, marginTop: 4,
    },
    tiles: {display: 'flex', gap: 12, marginBottom: 4},
    tileTitle: {fontWeight: 700, color: c.text, fontSize: 14, marginBottom: 2},
    tileSub: {fontSize: 12, color: c.sub, lineHeight: 1.35},
    actionRow: {display: 'flex', justifyContent: 'flex-end', marginTop: 20},
    rowBtn: {
        display: 'inline-flex', alignItems: 'center', gap: 6,
        border: `1px solid ${c.red}`, color: c.red, background: '#fff',
        borderRadius: 8, padding: '6px 12px', fontSize: 13, fontWeight: 600,
        cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
    },
    summaryCard: {
        border: `1px solid ${c.line}`, borderRadius: 12, padding: 16,
        background: c.soft, marginBottom: 20,
    },
    infoStrip: {
        display: 'flex', flexWrap: 'wrap',
        border: `1px solid ${c.line}`, borderRadius: 12,
        background: '#fff', overflow: 'hidden', marginBottom: 20,
    },
    infoCell: {
        flex: '1 1 160px', minWidth: 140, padding: '12px 16px',
        display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start',
    },
    microLabel: {
        fontSize: 11, fontWeight: 700, letterSpacing: '.04em',
        textTransform: 'uppercase', color: '#9ca3af',
    },
};

const titleStyles = theme => ({
    root: {margin: 0, padding: theme.spacing(2, 3)},
    closeButton: {position: 'absolute', right: theme.spacing(1.5), top: theme.spacing(1.5), color: theme.palette.grey[500]},
});

const DialogTitle = withStyles(titleStyles)(props => {
    const {children, classes, onClose, ...other} = props;
    return (
        <MuiDialogTitle disableTypography className={classes.root} {...other}>
            <Typography variant="h6" style={{fontWeight: 700}}>{children}</Typography>
            {onClose ? (
                <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
                    <CloseIcon className="close-button"/>
                </IconButton>
            ) : null}
        </MuiDialogTitle>
    );
});

const DialogContent = withStyles(theme => ({
    root: {padding: theme.spacing(3), background: '#fbfcfe'},
}))(MuiDialogContent);

const DialogActions = withStyles(theme => ({
    root: {margin: 0, padding: theme.spacing(2, 3), borderTop: `1px solid ${c.line}`},
}))(MuiDialogActions);

const OperationTile = ({active, disabled, accent, icon, title, subtitle, onClick}) => (
    <div
        onClick={disabled ? undefined : onClick}
        style={{
            flex: 1, border: `1.5px solid ${active ? accent : c.line}`, borderRadius: 10,
            padding: '12px 14px', cursor: disabled ? 'default' : 'pointer',
            background: active ? `${accent}0f` : '#fff', transition: 'all .15s',
            opacity: disabled ? 0.6 : 1,
        }}
    >
        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2}}>
            <span style={{...sx.tileTitle, color: active ? accent : c.text}}>
                <i className={icon} style={{marginRight: 8}}/>{title}
            </span>
            <i className={active ? 'fas fa-check-circle' : 'far fa-circle'} style={{color: active ? accent : '#cbd5e1'}}/>
        </div>
        <div style={sx.tileSub}>{subtitle}</div>
    </div>
);

function CancelRefundModal({
    transaction,
    orderId,
    userEmail,
    getProfile,
    manualPaymentCancel,
    clearManualPaymentCancel,
    cancelFetching,
    cancelData,
    cancelError,
}) {
    const [open, setOpen] = useState(false);
    const [operation, setOperation] = useState('cancel'); // 'cancel' = tam iptal | 'refund' = kısmi iade
    const [refundAmount, setRefundAmount] = useState('');
    const [message, setMessage] = useState('');
    const [validationError, setValidationError] = useState('');

    useEffect(() => {
        if (!userEmail && typeof getProfile === 'function') {
            getProfile();
        }
    }, [userEmail, getProfile]);

    const handleOpen = () => {
        setOperation('cancel');
        setRefundAmount('');
        setMessage('');
        setValidationError('');
        clearManualPaymentCancel();
        setOpen(true);
    };

    const handleClose = () => {
        if (cancelFetching) return;
        setOpen(false);
        clearManualPaymentCancel();
    };

    const isSuccess = cancelData && cancelData.status === 200 && !cancelData.error;

    const handleCloseAndReload = () => {
        if (cancelFetching) return;
        const shouldReload = isSuccess;
        setOpen(false);
        clearManualPaymentCancel();
        if (shouldReload) {
            setTimeout(() => window.location.reload(), 300);
        }
    };

    const buildParams = () => {
        const identifier = (transaction && transaction.code)
            ? {code: transaction.code}
            : {order_id: orderId};

        if (operation === 'cancel') {
            return {...identifier, status: 4, message: message || undefined};
        }
        return {...identifier, refund_amount: Number(refundAmount), message: message || undefined};
    };

    const handleApply = () => {
        setValidationError('');

        if (!transaction && !orderId) {
            setValidationError('İşlem bulunamadı.');
            return;
        }

        if (operation === 'refund') {
            const value = Number(refundAmount);
            if (!refundAmount || isNaN(value) || value <= 0) {
                setValidationError('Geçerli bir iade tutarı giriniz.');
                return;
            }
            if (transaction && Number(value) > Number(transaction.amount)) {
                setValidationError('İade tutarı işlem tutarından büyük olamaz.');
                return;
            }
        }

        clearManualPaymentCancel();
        manualPaymentCancel(buildParams());
    };

    // Yetki: sadece allowlist'teki e-postalar butonu görür
    if (!userEmail || !ALLOWED_EMAILS.includes(userEmail.toLowerCase())) {
        return null;
    }

    // İptal/İade edilmiş satırda buton gösterme (handoff: status=4'te pasif)
    if (transaction && transaction.status === 4) {
        return null;
    }

    const accent = operation === 'cancel' ? c.red : c.blue;

    const apiErrorMessage = (cancelData && cancelData.error && cancelData.message)
        || (cancelError && cancelError.response && cancelError.response.data && cancelError.response.data.message)
        || (cancelError && 'Bir hata oluştu. Lütfen tekrar deneyin.');

    const updatedRows = (isSuccess && cancelData.data && Array.isArray(cancelData.data.updated))
        ? cancelData.data.updated : [];

    const applyDisabled = cancelFetching || (operation === 'refund' && !refundAmount);

    return (
        <>
            <button type="button" onClick={handleOpen} style={sx.rowBtn}>
                <i className="fas fa-ban"/> İptal / İade
            </button>

            <Dialog
                onClose={handleClose}
                open={open}
                maxWidth="sm"
                fullWidth
                disableBackdropClick={true}
                disableEscapeKeyDown={cancelFetching}
                aria-labelledby="cancel-refund-title"
            >
                <DialogTitle className="red-bottom-border" id="cancel-refund-title" onClose={cancelFetching ? undefined : handleClose}>
                    İşlem İptal / Kısmi İade
                    <span style={{marginLeft: 10, fontSize: 13, fontWeight: 600, color: c.sub}}>{(transaction && transaction.code) || orderId || ''}</span>
                </DialogTitle>

                <DialogContent>
                    <div style={sx.infoBox}>
                        <i className="fas fa-info-circle" style={{color: c.blue, marginTop: 2}}/>
                        <span style={{fontSize: 13, color: '#334155', lineHeight: 1.5}}>
                            Bu ekran işlemi <strong>sistemde</strong> günceller (bankaya iade/void çağrısı yapmaz).
                            Tam iptalde durum <strong>İptal/İade</strong> olur; kısmi iadede durum korunur, sadece tutar düşer.
                        </span>
                    </div>

                    {transaction && (
                        <div style={sx.infoStrip}>
                            <div style={sx.infoCell}>
                                <span style={sx.microLabel}>İşlem Kodu</span>
                                <span style={{fontWeight: 700, color: c.text, fontSize: 14, fontFamily: 'monospace', wordBreak: 'break-all'}}>
                                    {transaction.code || orderId}
                                </span>
                            </div>
                            <div style={{...sx.infoCell, borderLeft: `1px solid ${c.line}`}}>
                                <span style={sx.microLabel}>Tutar</span>
                                <span style={{fontWeight: 700, color: c.text, fontSize: 16}}>
                                    {transaction.amount} <span style={{fontSize: 13, color: c.sub, fontWeight: 600}}>{transaction.currency}</span>
                                </span>
                            </div>
                            <div style={{...sx.infoCell, borderLeft: `1px solid ${c.line}`}}>
                                <span style={sx.microLabel}>Durum</span>
                                <StatusPill status={transaction.status}/>
                            </div>
                        </div>
                    )}

                    {/* ---- İşlem formu ---- */}
                    <div style={sx.card}>
                        <span style={sx.fieldLabel}>İşlem Tipi</span>
                        <div style={sx.tiles}>
                            <OperationTile
                                active={operation === 'cancel'}
                                disabled={cancelFetching}
                                accent={c.red}
                                icon="fas fa-ban"
                                title="Tam İptal"
                                subtitle="Durum İptal/İade olur"
                                onClick={() => { setOperation('cancel'); setValidationError(''); }}
                            />
                            <OperationTile
                                active={operation === 'refund'}
                                disabled={cancelFetching}
                                accent={c.blue}
                                icon="fas fa-hand-holding-usd"
                                title="Kısmi İade"
                                subtitle="Durum korunur, tutar düşer"
                                onClick={() => { setOperation('refund'); setValidationError(''); }}
                            />
                        </div>

                        {operation === 'refund' && (
                            <div style={{marginTop: 16}}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    label={`İade Tutarı${transaction ? ` (${transaction.currency})` : ''}`}
                                    placeholder="örn: 1000"
                                    value={refundAmount}
                                    onChange={(e) => { setRefundAmount(e.target.value.replace(/[^0-9.]/g, '')); setValidationError(''); }}
                                    disabled={cancelFetching}
                                />
                                {transaction && refundAmount && !isNaN(Number(refundAmount)) && (
                                    <div style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 12,
                                        background: c.soft, border: `1px solid ${c.line}`, borderRadius: 8, padding: '6px 12px',
                                    }}>
                                        <span style={{color: c.sub, fontSize: 13}}>Yeni tutar:</span>
                                        <span style={{fontWeight: 700, color: c.text}}>{transaction.amount}</span>
                                        <i className="fas fa-arrow-right" style={{color: c.sub, fontSize: 11}}/>
                                        <span style={{fontWeight: 700, color: c.blue}}>
                                            {(Number(transaction.amount) - Number(refundAmount)).toFixed(2)} {transaction.currency}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}

                        <TextField
                            fullWidth
                            multiline
                            rows={2}
                            variant="outlined"
                            size="small"
                            label="Not / Sebep"
                            placeholder="örn: Otel talebi - iptal"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            disabled={cancelFetching}
                            style={{marginTop: 20}}
                        />

                        {validationError && (
                            <Alert severity="error" style={{marginTop: 16}}>{validationError}</Alert>
                        )}

                        <div style={sx.actionRow}>
                            <Button
                                variant="contained"
                                onClick={handleApply}
                                disabled={applyDisabled}
                                startIcon={!cancelFetching && <i className={operation === 'cancel' ? 'fas fa-ban' : 'fas fa-hand-holding-usd'}/>}
                                style={{
                                    height: 44, minWidth: 180, textTransform: 'none', fontWeight: 700,
                                    borderRadius: 10, boxShadow: 'none',
                                    backgroundColor: applyDisabled ? '#e5e7eb' : accent,
                                    color: applyDisabled ? '#9ca3af' : '#fff',
                                }}
                            >
                                {cancelFetching
                                    ? <CircularProgress size={20} color="inherit"/>
                                    : (operation === 'cancel' ? 'İptal Et' : 'İade Uygula')}
                            </Button>
                        </div>
                    </div>

                    {/* ---- Sonuç ---- */}
                    {!validationError && cancelError && (
                        <Alert severity="error" style={{marginBottom: 20}}>{apiErrorMessage}</Alert>
                    )}
                    {!validationError && cancelData && !isSuccess && cancelData.error && (
                        <Alert severity="error" style={{marginBottom: 20}}>{cancelData.message || 'İşlem güncellenemedi.'}</Alert>
                    )}
                    {isSuccess && (
                        <Alert severity="success" style={{marginBottom: 16}}>
                            İşlem güncellendi. ({cancelData.data && cancelData.data.updated_count} kayıt)
                        </Alert>
                    )}
                    {isSuccess && updatedRows.length > 0 && (
                        <div style={sx.summaryCard}>
                            {updatedRows.map((row, i) => (
                                <div key={row.id} style={{paddingTop: i ? 12 : 0, marginTop: i ? 12 : 0, borderTop: i ? `1px solid ${c.lineSoft}` : 'none'}}>
                                    <div style={{fontWeight: 700, color: c.text, marginBottom: 6}}>{row.code || row.order_id || row.id}</div>
                                    <div style={{display: 'flex', flexWrap: 'wrap', gap: 20}}>
                                        <span style={{fontSize: 13, color: c.sub}}>Durum: <strong style={{color: c.text}}>{statusLabel(row.oldStatus)} → {statusLabel(row.newStatus)}</strong></span>
                                        <span style={{fontSize: 13, color: c.sub}}>Tutar: <strong style={{color: c.text}}>{row.oldAmount} → {row.newAmount}</strong></span>
                                        {(row.refundAmount !== undefined && row.refundAmount !== null) &&
                                            <span style={{fontSize: 13, color: c.sub}}>İade: <strong style={{color: c.blue}}>{row.refundAmount}</strong></span>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </DialogContent>

                <DialogActions>
                    {isSuccess ? (
                        <Button onClick={handleCloseAndReload} disabled={cancelFetching} variant="contained" color="primary" style={{textTransform: 'none', fontWeight: 700, height: 42, borderRadius: 10, boxShadow: 'none'}}>
                            Kapat & Yenile
                        </Button>
                    ) : (
                        <Button onClick={handleClose} disabled={cancelFetching} style={{textTransform: 'none', fontWeight: 600, height: 42}}>
                            Kapat
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
}

const mapStateToProps = (state) => ({
    userEmail: state.profileReducer && state.profileReducer.profileData
        && state.profileReducer.profileData.profile
        && state.profileReducer.profileData.profile.email,
    cancelFetching: state.manualPaymentCancelReducer && state.manualPaymentCancelReducer.cancelFetching,
    cancelData: state.manualPaymentCancelReducer && state.manualPaymentCancelReducer.cancelData,
    cancelError: state.manualPaymentCancelReducer && state.manualPaymentCancelReducer.cancelError,
});

const mapDispatchToProps = {
    getProfile,
    manualPaymentCancel,
    clearManualPaymentCancel,
};

export default connect(mapStateToProps, mapDispatchToProps)(CancelRefundModal);
